import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import type { Prayer } from '@flock-types/prayer.types';
import usePrayerSearch from '@/hooks/usePrayerSearch';

interface Props {
  groupId: string;
  /** Loaded prayers — quick search filters these client-side, and AI results
   *  resolve their wall card id against them for scroll-to. */
  prayers: Prayer[];
  onScrollToCard: (wallPrayerId: string) => void;
  onClose: () => void;
}

/** Normalized row shared by quick + AI modes. */
interface RowVM {
  key: string;
  wallId: string | null; // DOM card id to scroll to (null = not in loaded set)
  userName: string;
  text: string;
  date: Date | null;
  prayerType: string | null;
  status: string;
  matchPct?: number; // AI only
}

/** Strip the leading "[date | name | type | status]" embedding-context header
 * line that semantic results carry in prayerText. */
function stripEmbeddingHeader(text: string): string {
  if (!text) return '';
  const trimmed = text.trimStart();
  if (trimmed.startsWith('[')) {
    const end = trimmed.indexOf(']');
    if (end !== -1) return trimmed.slice(end + 1).trimStart();
  }
  return text;
}

/** Normalize similarityScore across the result set onto a 40–95% display range so
 * the worst match never shows a demoralizing single-digit score (Expo parity). */
function getMatchPercent(score: number, all: number[]): number {
  const max = Math.max(...all);
  const min = Math.min(...all);
  if (max === min) return 90;
  const ratio = (score - min) / (max - min);
  return Math.round(40 + ratio * 55);
}

function getMatchColor(pct: number): string {
  if (pct >= 70) return 'bg-green-100 text-green-700';
  if (pct >= 40) return 'bg-amber-100 text-amber-700';
  return 'bg-flock-navy-100 text-flock-navy-600';
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') return new Date(v);
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return null;
}

function ResultRow({ row, onSelect }: { row: RowVM; onSelect: (row: RowVM) => void }) {
  const when = row.date ? formatDistanceToNow(row.date, { addSuffix: true }) : '';
  return (
    <button
      onClick={() => onSelect(row)}
      className="block w-full rounded-lg px-3 py-2 text-left hover:bg-flock-navy-50"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-xs font-semibold text-flock-navy-700">{row.userName}</span>
        {row.matchPct !== undefined ? (
          <span
            className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${getMatchColor(row.matchPct)}`}
          >
            {row.matchPct}% match
          </span>
        ) : (
          when && <span className="shrink-0 text-[10px] text-flock-navy-400">{when}</span>
        )}
      </div>
      <div className="mt-0.5 line-clamp-2 text-xs text-flock-navy-600">{row.text}</div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-flock-navy-400">
        {row.prayerType && <span className="capitalize">{row.prayerType}</span>}
        {row.status === 'answered' && <span className="text-green-600">· Answered</span>}
        {row.matchPct !== undefined && when && <span>· {when}</span>}
      </div>
    </button>
  );
}

/**
 * Prayer search popover. Mirrors features/chat/search/ChatSearchPopover: a single
 * magnifying-glass entry opens this popover with a "Quick search / Ask AI" toggle.
 * Quick search filters the loaded prayers client-side; Ask AI hits the semantic
 * search endpoint. Both render result rows; clicking one scrolls to that card.
 */
export default function PrayerSearchPopover({ groupId, prayers, onScrollToCard, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'quick' | 'ai'>('quick');
  const ai = usePrayerSearch(groupId);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Quick search: instant client-side substring over loaded prayers (matches the
  // wall's previous inline filter), shown as rows.
  const quickResults: RowVM[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (mode !== 'quick' || !q) return [];
    return prayers
      .filter((p) => p.prayerText.toLowerCase().includes(q) || p.userName.toLowerCase().includes(q))
      .map((p) => ({
        key: p.id,
        wallId: p.id,
        userName: p.userName,
        text: p.prayerText,
        date: toDate(p.createdAt),
        prayerType: p.type,
        status: p.status,
      }));
  }, [prayers, query, mode]);

  const aiScores = ai.result?.prayers.map((p) => p.similarityScore) ?? [];
  const aiResults: RowVM[] = (ai.result?.prayers ?? []).map((r) => {
    const match = prayers.find((p) => p.id === r.prayerHistoryId || p.id === r.firestoreDocId);
    return {
      key: r.prayerHistoryId,
      wallId: match ? match.id : null,
      userName: r.userName,
      text: stripEmbeddingHeader(r.prayerText),
      date: r.createdAt ? new Date(r.createdAt) : null,
      prayerType: r.prayerType,
      status: r.status,
      matchPct: getMatchPercent(r.similarityScore, aiScores),
    };
  });

  const handleSelect = (row: RowVM) => {
    if (row.wallId) onScrollToCard(row.wallId);
    onClose();
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full z-40 mt-2 flex max-h-[70vh] w-96 flex-col rounded-xl border border-flock-gray-200 bg-white shadow-2xl"
    >
      <div className="flex items-center gap-2 border-b border-flock-gray-200 p-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && mode === 'ai') ai.search(query);
            }}
            placeholder={mode === 'quick' ? 'Search prayers' : 'Ask about prayers…'}
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-flock-navy-400"
          />
        </div>
        <button onClick={onClose} className="p-1.5 text-flock-navy-400 hover:text-flock-navy-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex gap-1 border-b border-flock-gray-200 px-2 py-1.5">
        <button
          onClick={() => setMode('quick')}
          className={`rounded-full px-3 py-1 text-xs font-medium ${mode === 'quick' ? 'bg-flock-navy-600 text-white' : 'text-flock-navy-500 hover:bg-flock-navy-50'}`}
        >
          Quick search
        </button>
        <button
          onClick={() => {
            setMode('ai');
            if (query.trim()) ai.search(query);
          }}
          className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${mode === 'ai' ? 'bg-flock-navy-600 text-white' : 'text-flock-navy-500 hover:bg-flock-navy-50'}`}
        >
          <SparklesIcon className="h-3.5 w-3.5" /> Ask AI
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {mode === 'quick' && !query.trim() && (
          <div className="p-3 text-xs text-flock-navy-400">
            Type to filter this group&apos;s prayers by text or name.
          </div>
        )}

        {mode === 'ai' && !ai.result && !ai.loading && !ai.error && (
          <div className="p-3 text-xs text-flock-navy-400">
            <div className="flex items-center gap-1.5 font-medium text-flock-navy-600">
              <SparklesIcon className="h-4 w-4" /> AI-powered prayer search
            </div>
            <p className="mt-1">
              Describe what you remember — a name, a topic, a feeling — and press Enter.
            </p>
            {ai.recentSearches.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-semibold uppercase tracking-wide">Recent</span>
                  <button
                    onClick={ai.clearRecentSearches}
                    className="text-[10px] text-flock-navy-400 hover:text-flock-navy-600"
                  >
                    Clear
                  </button>
                </div>
                {ai.recentSearches.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setQuery(s);
                      ai.search(s);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === 'ai' && ai.loading && (
          <div className="p-4 text-center text-sm text-flock-navy-400">Searching prayers…</div>
        )}
        {mode === 'ai' && ai.error && (
          <div className="p-4 text-center text-sm text-red-600">{ai.error}</div>
        )}

        {mode === 'ai' && ai.result?.answer && (
          <div className="mb-2 rounded-lg bg-flock-navy-50 px-3 py-2 text-xs text-flock-navy-800">
            {ai.result.answer}
          </div>
        )}

        {mode === 'ai' && ai.result && ai.result.prayers.length === 0 && !ai.loading && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            No matches found. Try describing what you remember differently.
          </div>
        )}
        {mode === 'quick' && query.trim() && quickResults.length === 0 && (
          <div className="p-4 text-center text-sm text-flock-navy-400">No prayers found.</div>
        )}

        {(mode === 'quick' ? quickResults : aiResults).map((row) => (
          <ResultRow key={row.key} row={row} onSelect={handleSelect} />
        ))}
      </div>
    </div>
  );
}
